import Box from '@mui/material/Box';
import BugReportIcon from '@mui/icons-material/BugReport';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router';
import { mindtimeWazuhMalwareApi } from 'src/api/mindtime/wazuhMalwareApi';
import { Table } from 'src/common';
import { getSecurityMalwareDetailHref } from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import type {
  WazuhMalwareAgentSummaryData,
  WazuhMalwareDetectionItem,
  WazuhMalwareDetectionsQuery,
} from 'src/types/mindtime/wazuhMalware';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import {
  getMalwareDetectionId,
  getMalwareDetectionListColumns,
  MALWARE_SEVERITY_FILTER_OPTIONS,
} from '../../malware-detection/malwareDetailModel';
import { getAgentMalwarePreview } from '../../preview/malware';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrKpiCard,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../../shared/edrPrimitives';

const PAGE_SIZE = 25;

type Filters = { severityBand?: string; malwareType?: string };


const buildListQuery = (
  systemWazuhAgentId: string,
  page: number,
  pageSize: number,
  search: string,
  filters: Filters
): WazuhMalwareDetectionsQuery => ({
  systemWazuhAgentId,
  page,
  pageSize,
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(filters.severityBand ? { severityBand: filters.severityBand } : {}),
  ...(filters.malwareType ? { malwareType: filters.malwareType } : {}),
});

type EndpointMalwareTabProps = {
  systemWazuhAgentId: string;
};

const EndpointMalwareTab: FC<EndpointMalwareTabProps> = ({ systemWazuhAgentId }) => {
  const navigate = useNavigate();
  const { wait, isPreviewMode } = useEdrDataGate('endpoints');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'malware-detection');
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const agentId = systemWazuhAgentId.trim();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const [typeFacets, setTypeFacets] = useState<string[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [summary, setSummary] = useState<WazuhMalwareAgentSummaryData | null>(null);
  const [items, setItems] = useState<WazuhMalwareDetectionItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(0);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const openMalwareDetail = useCallback(
    (item: WazuhMalwareDetectionItem) => {
      const id = getMalwareDetectionId(item);
      if (!id) return;
      navigate(getSecurityMalwareDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  useEffect(() => {
    if (!agentId) {
      setSummary(null);
      return undefined;
    }
    if (wait) return undefined;
    if (isPreviewMode) {
      setSummary(canSummary ? getAgentMalwarePreview(agentId).summary : null);
      return undefined;
    }

    if (!canSummary) {
      setSummary(null);
      return undefined;
    }

    let cancelled = false;
    mindtimeWazuhMalwareApi
      .getAgentSummary(agentId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'EndpointMalwareTab.summary');
          setSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, wait, isPreviewMode, canSummary]);

  useEffect(() => {
    if (!agentId) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }
    if (wait) {
      setListLoading(true);
      return undefined;
    }
    if (isPreviewMode) {
      if (!canList) {
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
        return undefined;
      }
      const preview = getAgentMalwarePreview(agentId);
      setItems(preview.items);
      setTotalRecords(preview.items.length);
      setListLoading(false);
      return undefined;
    }

    if (!canList) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }

    let cancelled = false;
    setListLoading(true);
    mindtimeWazuhMalwareApi
      .getDetections(buildListQuery(agentId, page, rowsPerPage, search, filters))
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items ?? []);
        setTotalRecords(data?.totalRecords ?? 0);
        setListLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logger(error, 'error', 'EndpointMalwareTab.list');
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    page,
    rowsPerPage,
    search,
    filters.severityBand,
    filters.malwareType,
    wait,
    isPreviewMode,
    canList,
  ]);

  useEffect(() => {
    setTypeFacets([]);
  }, [agentId]);

  useEffect(() => {
    if (!items.length) return;
    setTypeFacets((prev) => {
      const next = new Set(prev);
      items.forEach((item) => {
        const type = item.malwareType?.trim();
        if (type) next.add(type);
      });
      return Array.from(next).sort((a, b) => a.localeCompare(b));
    });
  }, [items]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        id: getMalwareDetectionId(item) || item.threatName,
      })),
    [items]
  );

  const setFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters({});
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: MALWARE_SEVERITY_FILTER_OPTIONS,
        value: filters.severityBand ?? '',
        multiple: true,
        onChange: (value) => setFilter('severityBand', value),
      },
      {
        id: 'type',
        label: 'Malware Type',
        options: typeFacets.map((name) => ({ label: name, value: name })),
        value: filters.malwareType ?? '',
        multiple: true,
        onChange: (value) => setFilter('malwareType', value),
      },
    ],
    [filters.severityBand, filters.malwareType, typeFacets]
  );

  const columns: Column[] = useMemo(() => getMalwareDetectionListColumns('endpoint'), []);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      {canSummary ? (
      <Box
        display="grid"
        style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, alignItems: 'stretch' }}
      >
        <EdrKpiCard
          label="Total Detections"
          value={formatEdrNumber(summary?.totalDetections)}
          icon={<BugReportIcon style={{ fontSize: 18 }} />}
          iconColor={EDR_COLORS.critical}
          accent={EDR_COLORS.critical}
        />
        <EdrKpiCard
          label="Detections Today"
          value={formatEdrNumber(summary?.detectionsToday)}
          icon={<CalendarTodayIcon style={{ fontSize: 18 }} />}
          iconColor={EDR_COLORS.high}
          accent={EDR_COLORS.high}
        />
      </Box>
      ) : null}

      {canList ? (
      <EdrDataCard
        toolbar={(
          <EdrDataToolbar
            searchValue={searchInput}
            onSearchChange={(value) => {
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            searchPlaceholder="Search detections…"
            filters={toolbarFilters}
            onReset={handleReset}
          />
        )}
      >
        <Table
          columns={columns}
          data={rows as unknown as TableData[]}
          loading={listLoading}
          showPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(0);
          }}
          onRowClick={(row) => openMalwareDetail(row as unknown as WazuhMalwareDetectionItem)}
          TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          MUITableProps={{ sx: { tableLayout: 'fixed', width: '100%' } }}
        />
      </EdrDataCard>
      ) : null}
    </Box>
  );
};

export default EndpointMalwareTab;
